import Image from 'next/image';
import { useRef, useState, useEffect } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import { Box, Stack, Button, useTheme, Typography } from '@mui/material';

import { bearerAuthHeader } from 'src/lib/firebase/auth-kit';
import { RADIUS, SHADOW, TEXT_WRAP } from 'src/theme/ui-tokens';

// 클라이언트에서 서버 액션 직접 호출 금지: 내부 API로 우회

/**
 * 빈 상태 표시. 타입별로 쓰는 props가 달라 **전부 옵셔널**이다.
 *
 * ⚠️ JSDoc이 없으면 TS 호출부에서 모든 props가 필수로 추론된다(`.jsx` 컴포넌트의 기본 함정).
 * `= undefined` 기본값으로도 풀리지만 그러면 타입이 `undefined`로 굳어 **값을 넘길 수 없다** —
 * 실제로 값이 오는 props는 이렇게 타입을 적어야 한다. 이 파일이 `.tsx`가 되면 사라진다.
 *
 * @param {Object} props
 * @param {string} [props.type] 'default' | 'api-error' | 'suggest' | 'market-filter' | 'complex-keyword' | 'empty-with-action'
 * @param {React.ReactNode} [props.title]
 * @param {React.ReactNode} [props.description]
 * @param {Function} [props.onSuggest]
 * @param {string} [props.imageSrc]
 * @param {string} [props.searchKeyword] suggest 타입일 때 쓰는 검색 키워드
 * @param {string} [props.query] 연산자 오류(complex-keyword)일 때의 쿼리 원문
 * @param {string[]} [props.targetLanguages] 선택 국가에서 유도된 언어 코드
 * @param {Object} [props.sx]
 * @param {string} [props.buttonText] empty-with-action 전용
 * @param {Function} [props.onActionClick] empty-with-action 전용
 * @param {string} [props.icon]
 */
export const NoData = ({
  type = 'default', // 'default' | 'api-error' | 'suggest' | 'market-filter' | 'complex-keyword' | 'empty-with-action'
  title,
  description,
  // ⚠️ `= undefined`는 런타임에 아무 일도 하지 않는다. **TS를 위한 표시다** —
  //    기본값이 없으면 TS가 이 props를 필수로 추론해, 이 컴포넌트를 쓰는 모든 `.tsx`가
  //    컴파일 에러가 난다 (타입별로 실제로는 생략하는 값이다).
  //    이 파일이 `.tsx`로 넘어오면 `?`로 바뀌고 이 줄들은 사라진다.
  onSuggest = undefined,
  imageSrc = undefined,
  searchKeyword = '', // suggest 타입일 때 사용할 검색 키워드 (일반 케이스)
  query = '', // 연산자 오류(complex-keyword)일 때 전달받는 쿼리 스트링 원문
  targetLanguages = [], // 선택된 국가로부터 유도된 언어 코드 배열 (예: ['kr','us'])
  sx = {},
  // empty-with-action 타입용: 아이콘 + 버튼 액션
  buttonText = '',
  onActionClick,
  icon = 'solar:add-folder-linear',
}: {
  type?: 'default' | 'api-error' | 'suggest' | 'market-filter' | 'complex-keyword' | 'empty-with-action'
  title?: React.ReactNode
  description?: React.ReactNode
  onSuggest?: (suggestedKeyword: string) => void
  imageSrc?: string
  searchKeyword?: string
  query?: string
  targetLanguages?: string[]
  sx?: Record<string, unknown>
  buttonText?: string
  onActionClick?: () => void
  icon?: string
}) => {
  const [multiLangSuggestions, setMultiLangSuggestions] = useState<Record<string, string> | null>(null);
  const [isLoadingMultiLang, setIsLoadingMultiLang] = useState(false);
  const [operatorSuggestions, setOperatorSuggestions] = useState<{ query?: string; explanation?: string }[]>([]);
  const [isLoadingOperator, setIsLoadingOperator] = useState(false);
  const lastTranslationKeyword = useRef('');

  // 반응형을 위한 미디어 쿼리
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 타입별 기본 설정
  // 'suggest' 키는 없다 — 그 갈래는 default 로 폴백한다
  const typeConfig: Partial<Record<NonNullable<typeof type>, { title: string; description: string; imageSrc: string }>> & { default: { title: string; description: string; imageSrc: string } } = {
    // 기본 타입 : 데이터가 없음 - 과도한 필터/오타
    default: {
      title: '데이터가 없습니다',
      description: '요청하신 데이터를 찾을 수 없습니다. 필터 및 검색 조건이 엄격할 수 있습니다.',
      imageSrc: '/assets/images/catchmoments/no-data-image2.png',
    },
    // API 호출 실패 시 : 결과 화면을 불러오지 못했을 때 디폴트 노출
    'api-error': {
      title: '데이터가 없습니다',
      description:
        '일시적으로 데이터를 불러오지 못했을 수 있습니다.\n잠시 후 다시 시도해 주세요.',
      imageSrc: '/assets/images/catchmoments/no-data-image2.png',
    },
    // 국가 필터 : 선택한 국가에 대한 데이터 없음
    'market-filter': {
      title: '선택하신 국가에서 데이터를 찾을 수 없습니다',
      description:
        '다른 국가를 선택하거나 국가 필터를 해제해보세요.',
      imageSrc: '/assets/images/catchmoments/no-data-image2.png',
    },
    // 복합 검색어 : 띄어쓰기가 포함되어 있거나, 연산자가 포함되어 있을 경우
    'complex-keyword': {
      title: '복합 검색어에 대한 결과가 없습니다',
      description:
        '정확히 일치하는 결과만 표시됩니다.\n더 간단한 키워드로 검색하거나 조건을 조정해보세요.',
      imageSrc: '/assets/images/catchmoments/no-data-image2.png',
    },
    // 아이콘 + 제목/설명 + 액션 버튼 (예: 브랜드 키워드 없음 → 브랜드 등록 버튼)
    'empty-with-action': {
      title: '데이터가 없습니다',
      description: '등록 후 이용해 보세요.',
      imageSrc: '/assets/images/catchmoments/no-data-image2.png',
    },
  };

  // (과거 UI 표시용 포맷터는 현재 미사용)

  // 연산자 오류(complex-keyword) 시에는 쿼리 원문(query)을 로깅/활용, 그 외에는 searchKeyword 사용
  useEffect(() => {
    const raw = type === 'complex-keyword' ? query ?? '' : searchKeyword ?? '';
    // 그대로 출력
    if (type === 'complex-keyword') {
      // 1) 서버에 사용된 원문 그대로
      console.log('연산자 오류시 query--------------------------', raw);
      // 2) 화면 가독성을 위해 이스케이프 해제 버전도 참고 로그
      const unescaped = String(raw).replace(/\\"/g, '"');
      console.log('연산자 오류시 query(unescaped)--------------', unescaped);
    } else {
      console.log('NoData 검색 키워드--------------------------', raw);
    }
  }, [type, query, searchKeyword]);

  // 연산자 오류(complex-keyword) 시 translateOperator API 호출 및 요청/응답 로깅
  useEffect(() => {
    if (type !== 'complex-keyword') return;
    const raw = String(query ?? '').trim();
    if (!raw) return;

    console.log('🔎 [NoData] translateOperator 요청 ->', { query: raw });
    setIsLoadingOperator(true);
    setOperatorSuggestions([]);
    // LLM route 는 로그인 토큰 필수 (A523)
    bearerAuthHeader()
      .then((authHeader) =>
        fetch('/api/ai/translate-operator', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({ text: raw }),
        })
      )
      .then((r) => r.json())
      .then((res) => {
        console.log('✅ [NoData] translateOperator 응답 <-', res);
        if (res?.success && Array.isArray(res?.data?.suggestions)) {
          setOperatorSuggestions(res.data.suggestions);
        } else {
          setOperatorSuggestions([]);
        }
      })
      .catch(() => {
        console.error('❌ [NoData] translateOperator 호출 실패');
        setOperatorSuggestions([]);
      })
      .finally(() => {
        setIsLoadingOperator(false);
      });
  }, [type, query]);

  // 현재 타입의 설정 가져오기 (props로 override 가능)
  const config = typeConfig[type] || typeConfig.default;
  const effectiveTitle = title ?? config.title;
  const effectiveDescription = description ?? config.description;
  const effectiveImageSrc = imageSrc ?? config.imageSrc;
  const effectiveButtonText = buttonText || (type === 'empty-with-action' ? '등록하기' : '');

  // market-filter 타입일 때 다국어 번역 제안(API 호출)
  useEffect(() => {
    const fetchTranslations = async () => {
      const trimmedKeyword = searchKeyword?.trim() || '';

      if (lastTranslationKeyword.current === trimmedKeyword) {
        return;
      }

      if (type === 'market-filter' && trimmedKeyword) {
        lastTranslationKeyword.current = trimmedKeyword;
        setIsLoadingMultiLang(true);
        setMultiLangSuggestions(null);
        try {
          const resp = await fetch('/api/ai/translate-multi', {
            method: 'POST',
            // LLM route 는 로그인 토큰 필수 (A523)
            headers: { 'Content-Type': 'application/json', ...(await bearerAuthHeader()) },
            body: JSON.stringify({ text: trimmedKeyword, targetLanguageCodes: targetLanguages }),
          });
          const result = await resp.json();
          // ⚠️ `data` 는 `lib/ai` 경계가 파싱·세척까지 끝낸 «객체»다 — 그대로 쓴다.
          //    ```json 펜스를 걷거나 다시 파싱하지 않는다.
          if (result.success && result.data && typeof result.data === 'object') {
            setMultiLangSuggestions(result.data);
          } else {
            setMultiLangSuggestions(null);
          }
        } catch {
          console.error('❌ [NoData] 다국어 번역 제안 실패');
          setMultiLangSuggestions(null);
        } finally {
          setIsLoadingMultiLang(false);
        }
      } else {
        setMultiLangSuggestions(null);
        lastTranslationKeyword.current = '';
      }
    };

    fetchTranslations();
  }, [type, searchKeyword, targetLanguages]);

  // 추천 키워드 클릭 핸들러
  const handleSuggestionClick = (suggestedKeyword: string) => {
    if (onSuggest) {
      onSuggest(suggestedKeyword);
    }
  };

  // empty-with-action: 이미지 + 제목/설명 + 액션 버튼 (default와 동일 이미지 사용)
  if (type === 'empty-with-action') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 3,
          textAlign: 'center',
          ...sx,
        }}
      >
        <Box sx={{ mb: isMobile ? 2 : 3 }}>
          <Image
            src={effectiveImageSrc}
            alt="No data"
            width={isMobile ? 150 : 200}
            height={isMobile ? 112 : 125}
            style={{
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: isMobile ? '30px' : '60px',
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary', ...TEXT_WRAP.title }}>
          {effectiveTitle}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', maxWidth: 400, lineHeight: 1.7, ...TEXT_WRAP.title }}>
          {effectiveDescription}
        </Typography>
        {effectiveButtonText && typeof onActionClick === 'function' && (
          <Button
            variant="contained"
            color="primary"
            onClick={onActionClick}
            sx={{
              borderRadius: RADIUS.lg,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.5,
            }}
          >
            {effectiveButtonText}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
        ...sx,
      }}
    >
      {/* 이미지 */}
      <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Image
          src={effectiveImageSrc}
          alt="No data"
          width={isMobile ? 150 : 200}
          height={isMobile ? 112 : 125}
          style={{
            maxWidth: '100%',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: isMobile ? '30px' : '60px',
          }}
        />
      </Box>

      {/* 제목 */}
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: 'text.primary',
          ...TEXT_WRAP.title,
        }}
      >
        {effectiveTitle}
      </Typography>

      {/* 설명 */}
      <Typography
        variant="body2"
        sx={{
          mb: 4,
          color: 'text.secondary',
          maxWidth: 600,
          lineHeight: 2,
          whiteSpace: 'pre-line',
          // 가운데 정렬 안내문 — 줄 길이를 고르게(「…관리해 / 보세요.」처럼 한 낱말만 떨어지지 않게)
          ...TEXT_WRAP.title,
        }}
      >
        {effectiveDescription}
      </Typography>


      {/* 복합 쿼리 추천 (연산자 제안) */}
      {type === 'complex-keyword' && (
        <Box sx={{ mb: 4, width: '100%', maxWidth: 600 }}>
          {isLoadingOperator ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, mt: 2 }}>
              추천 검색어 찾는 중...
            </Typography>
          ) : (
            operatorSuggestions.length > 0 && (
              <>
                <Typography sx={{ color: 'black', mb: 2, mt: 2, fontWeight: 600, fontSize: '15px' }}>
                  이런 키워드를 찾고 싶은가요?
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                  {operatorSuggestions.map((s, idx) => (
                    <Button
                      size="small"
                      key={idx}
                      onClick={() => handleSuggestionClick(s?.query ?? '')}
                      sx={{
                        borderRadius: RADIUS.lg,
                        fontSize: '13.5px',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.5,
                        border: '1px solid',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        bgcolor: 'white',
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white',
                          borderColor: 'primary.dark',
                          transform: 'translateY(-1px)',
                          boxShadow: SHADOW.overlay,
                        },
                      }}
                    >
                      {s?.explanation ?? s?.query}
                    </Button>
                  ))}
                </Stack>
              </>
            )
          )}
        </Box>
      )}

      {/* 추천 키워드 섹션 (다국어) */}
      {type === 'market-filter' && searchKeyword && (
        <Box sx={{ mb: 4, width: '100%', maxWidth: 500 }}>
          {isLoadingMultiLang ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, mt: 10 }}>
              비슷한 키워드 찾는 중...
            </Typography>
          ) : (
            multiLangSuggestions &&
            Object.keys(multiLangSuggestions).length > 0 && (
              <>
                <Typography
                  sx={{ color: 'black', mb: 2, mt: 10, fontWeight: 600, fontSize: '15px' }}
                >
                  다른 국가에서는 이런 키워드로 사용되고 있어요!
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                  {Object.entries(multiLangSuggestions).map(([lang, keyword]) => (
                    <Button
                      size="small"
                      key={lang}
                      onClick={() => handleSuggestionClick(keyword)}
                      sx={{
                        borderRadius: RADIUS.lg,
                        fontSize: '13.5px',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.5,
                        border: '1px solid',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        bgcolor: 'white',
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white',
                          borderColor: 'primary.dark',
                          transform: 'translateY(-1px)',
                          boxShadow: SHADOW.overlay,
                        },
                      }}
                    >
                      {keyword}
                    </Button>
                  ))}
                </Stack>
              </>
            )
          )}
        </Box>
      )}
    </Box>
  );
};